import React, { useState, useEffect, useCallback } from 'react';
import * as S from './AdvancesStyles';
import OverviewTab from './OverviewTab';
import LedgerTab from './LedgerTab';
import ApprovalsTab from './ApprovalsTab';
import ApiaryPnLTab from './ApiaryPnLTab';
import ProjectPnLTab from './ProjectPnLTab';
import PayrollTab from './PayrollTab';
import ReportingMappingTab from './ReportingMappingTab';
import AdvancesTable from './AdvancesTable';
import AdvanceRequestsTab from './AdvanceRequestsTab';
import NectarExpenses from '../Apiaries/ApiaryDetails/Components/NectarExpenses';
import PassbookDrawer from './PassbookDrawer';
import AddEntryModal from './AddEntryModal';
import SettleModal from './SettleModal';
import ExpenseDetailModal from './ExpenseDetailModal';
import {
  getAllAdvances,
  addAdvance,
  getSupervisors,
  getAllApiariesService,
  getAllExpensesGlobal,
  getAllProjectsService,
  getAdvancesOverviewService,
  updateExpenseApprovalStatus,
  bulkApproveExpensesService,
  addExpense,
  addNonApiaryExpense,
  createPayrollBatchService,
  settleAdvanceService
} from '../../../../services/apiaryService';
import { checkIsAdmin } from './authHelper';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'ledger', label: 'Ledger' },
  { id: 'approvals', label: 'Approvals' },
  { id: 'advances', label: 'Advances' },
  { id: 'expenses', label: 'Expenses' },
  { id: 'apiary', label: 'Apiary P&L' },
  { id: 'project', label: 'Project P&L' },
  { id: 'payroll', label: 'Payroll Export' },
  { id: 'reporting', label: 'Reporting Mapping' },
];

const TITLES = {
  overview: ['Overview', 'A single source of truth for advances, expenses and reimbursements.'],
  ledger: ['Ledger', 'Every advance, expense and settlement — one table, fully filterable.'],
  approvals: ['Approvals', 'Review pending expenses before they hit anyone\'s balance.'],
  advances: ['Advances', 'Advances issued to staff, independently or linked to an apiary.'],
  expenses: ['Expenses', 'Every logged expense, apiary and non-apiary, with allocation and approval status.'],
  apiary: ['Apiary P&L', 'Cost breakdown for a single apiary.'],
  project: ['Project P&L', 'Rollup of every apiary plus non-apiary overhead for a project.'],
  payroll: ['Payroll Export', 'Settle reimbursement payables — individually or as a batch.'],
  reporting: ['Reporting Hierarchy & Managers', 'Map employees to their respective Reporting Managers for automatic expense tagging.']
};

const AdvancesExpensesHub = () => {
  const isAdmin = checkIsAdmin();
  const [activeTab, setActiveTab] = useState('overview');
  const [advanceView, setAdvanceView] = useState('issued'); // issued | requests
  const [pendingAdvanceRequests, setPendingAdvanceRequests] = useState(0);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [apiaries, setApiaries] = useState([]);
  const [txns, setTxns] = useState([]);
  const [payrollBatches, setPayrollBatches] = useState([]);

  // Drawer and Modal States
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activePassbookMemberId, setActivePassbookMemberId] = useState(null);

  const [addEntryShow, setAddEntryShow] = useState(false);
  const [addEntryInitialType, setAddEntryInitialType] = useState('expense');
  const [addEntryInitialMemberId, setAddEntryInitialMemberId] = useState('');

  const [settleModalShow, setSettleModalShow] = useState(false);
  const [settleMemberId, setSettleMemberId] = useState(null);

  const [detailModalItem, setDetailModalItem] = useState(null);

  // Fetch real supervisors, apiaries, advances & expenses from backend APIs
  useEffect(() => {
    const loadBackendData = async () => {
      setLoading(true);
      try {
        const [sups, apis, advs, exps, projs] = await Promise.all([
          getSupervisors().catch(() => []),
          getAllApiariesService().catch(() => []),
          getAllAdvances().catch(() => []),
          getAllExpensesGlobal().catch(() => []),
          getAllProjectsService().catch(() => [])
        ]);

        // Only keep active, non-closed projects
        const activeProjs = (projs || []).filter(p => {
          if (p.is_active === false || p.isActive === false) return false;
          if (p.deleted === true || p.isDeleted === true) return false;
          const st = String(p.status || '').toUpperCase();
          if (st === 'CLOSED' || st === 'INACTIVE' || st === 'DISABLED' || st === 'ARCHIVED' || st === 'COMPLETED') return false;
          return true;
        });

        let mappedProjects = activeProjs.map(p => {
          const pId = String(p._id || p.projectId || p.id);
          const pName = p.project_name || p.name || 'Project';
          const pStates = Array.isArray(p.states) ? p.states : (p.state ? [p.state] : []);
          const pDistricts = Array.isArray(p.districts) ? p.districts : (p.district ? [p.district] : []);
          return {
            id: pId,
            name: pName,
            states: pStates.map(s => String(s).toLowerCase().trim()),
            districts: pDistricts.map(d => String(d).toLowerCase().trim()),
            raw: p
          };
        });

        if (mappedProjects.length === 0) {
          const projectIds = new Set();
          (apis || []).forEach(a => {
            const pId = a.projectId || a.project_id;
            if (pId) projectIds.add(String(pId));
          });
          if (projectIds.size > 0) {
            mappedProjects = Array.from(projectIds).map((pId, idx) => ({
              id: pId,
              name: `Project ${idx + 1}`,
              states: [],
              districts: []
            }));
          } else {
            mappedProjects = [{ id: 'p1', name: 'Main Project', states: [], districts: [] }];
          }
        }
        const defaultProjId = mappedProjects[0]?.id || 'p1';

        setProjects(mappedProjects);

        // Helper to match state / district / text to project
        const findProjectIdByGeo = (stateVal, districtVal, nameVal = '') => {
          const s = String(stateVal || '').toLowerCase().trim();
          const d = String(districtVal || '').toLowerCase().trim();
          const n = String(nameVal || '').toLowerCase().trim();

          for (const proj of mappedProjects) {
            const pName = proj.name.toLowerCase();
            const pId = proj.id.toLowerCase();

            if (s && (pName.includes(s) || pId.includes(s) || proj.states.includes(s))) {
              return proj.id;
            }
            if (d && (pName.includes(d) || proj.districts.includes(d))) {
              return proj.id;
            }
            if (n && (n.includes(pName) || (proj.states[0] && n.includes(proj.states[0])))) {
              return proj.id;
            }
          }
          return null;
        };

        const advanceIssuedUserIds = new Set(
          (advs || []).map(a => String(a.issued_to?._id || a.issued_to))
        );

        let memberList = [];
        if (sups && sups.length > 0) {
          memberList = sups.map(s => {
            const sId = String(s._id || s.id);
            const hasAdvance = advanceIssuedUserIds.has(sId);
            const directSupProjId = s.projectId || s.project_id || s.adminMetaInfo?.projectId || s.userMetaInfo?.projectId || (s.fieldViewData && s.fieldViewData[0]?.project_id) || null;
            const geoSupProjId = directSupProjId ? null : findProjectIdByGeo(s.state, s.district, s.name || s.username);
            const supProjId = directSupProjId || geoSupProjId || null;
            return {
              id: s._id || s.id,
              name: s.name || s.username || 'Staff',
              role: s.role || (s.userRoles && s.userRoles[0]) || 'Supervisor',
              projectId: supProjId ? String(supProjId) : null,
              state: s.state || null,
              district: s.district || null,
              mode: hasAdvance ? 'advance' : 'reimbursement'
            };
          });
        }

        // Dynamically add members from expenses/advances if not in supervisor list
        const knownMemberIds = new Set(memberList.map(m => String(m.id)));
        (exps || []).forEach(e => {
          const pId = e.paid_by?._id || e.paid_by || e.expensed_by;
          if (pId && !knownMemberIds.has(String(pId))) {
            const pName = e.paid_by?.name || e.paid_by_name || e.created_by_name || 'Staff Member';
            const hasAdvance = advanceIssuedUserIds.has(String(pId));
            const pProjId = e.project_id || e.projectId || findProjectIdByGeo('', '', pName) || null;
            memberList.push({ id: pId, name: pName, role: 'Staff', projectId: pProjId ? String(pProjId) : null, mode: hasAdvance ? 'advance' : 'reimbursement' });
            knownMemberIds.add(String(pId));
          }
        });
        (advs || []).forEach(a => {
          const iId = a.issued_to?._id || a.issued_to;
          if (iId && !knownMemberIds.has(String(iId))) {
            const iName = a.issued_to_name || a.issued_to?.name || 'Staff Member';
            memberList.push({ id: iId, name: iName, role: 'Staff', projectId: null, mode: 'advance' });
            knownMemberIds.add(String(iId));
          }
        });
        setMembers(memberList);

        const mappedApis = (apis || []).map(a => {
          const directProjId = a.projectId || a.project_id || a.clusterId?.projectId || a.cluster_id?.projectId || null;
          const supId = a.supervisor_id?._id || a.supervisor_id || a.created_by?._id || a.created_by;
          const supObj = supId ? memberList.find(s => String(s.id) === String(supId)) : null;
          const geoProjId = findProjectIdByGeo(a.state, a.district, a.apiary_name || a.name);
          const resolvedProjId = directProjId || supObj?.projectId || geoProjId || null;
          const apiaryDomainId = a.apiary_id || a._id;
          return {
            id: String(apiaryDomainId),
            apiary_id: a.apiary_id ? String(a.apiary_id) : String(apiaryDomainId),
            mongoId: a._id ? String(a._id) : null,
            name: a.apiary_name || a.name || 'Apiary',
            projectId: resolvedProjId ? String(resolvedProjId) : null,
            supervisorId: supId ? String(supId) : null,
            district: a.district || null,
            state: a.state || null
          };
        });
        setApiaries(mappedApis);

        let liveTxns = [];
        if (advs && advs.length > 0) {
          const mappedAdvs = advs.map((a, i) => {
            const advMemberId = a.issued_to?._id || a.issued_to;
            const supObj = memberList.find(s => String(s.id) === String(advMemberId));
            const advDate = a.date
              ? new Date(a.date).toISOString().split('T')[0]
              : a.created_at
              ? new Date(a.created_at).toISOString().split('T')[0]
              : '2026-08-01';

            const advTargetApiary = a.apiary_id ? mappedApis.find(ap => String(ap.id) === String(a.apiary_id) || String(ap.apiary_id) === String(a.apiary_id)) : null;
            const advProjId = a.project_id || a.projectId || advTargetApiary?.projectId || supObj?.projectId || null;

            // Settlement records reduce the balance. New ones have type SETTLEMENT; older ones
            // were saved as an ADVANCE with an "S..." id and SETTLED status.
            const isSettlement =
              String(a.type || '').toUpperCase() === 'SETTLEMENT' ||
              (String(a.status || '').toUpperCase() === 'SETTLED' &&
                String(a.advance_id_display || '').startsWith('S'));

            return {
              id: a._id || `ADV_${i}`,
              advance_id_display: a.advance_id_display || `ADV_${i}`,
              type: isSettlement ? 'advance_settled' : 'advance_issued',
              date: advDate,
              memberId: advMemberId,
              memberName: a.issued_to_name || a.issued_to?.name || supObj?.name || 'Staff',
              user_employee_id: a.user_employee_id || a.employee_id || a.issued_to_employee_id || a.paid_by_employee_id || supObj?.employee_id || null,
              employee_id: a.user_employee_id || a.employee_id || a.issued_to_employee_id || a.paid_by_employee_id || supObj?.employee_id || null,
              amount: Number(a.amount) || 0,
              locId: a.apiary_id || null,
              projectId: advProjId ? String(advProjId) : null,
              comment: a.comments || ''
            };
          });
          liveTxns = [...liveTxns, ...mappedAdvs];
        }

        if (exps && exps.length > 0) {
          const mappedExps = exps.map((e, i) => {
            const expMemberId = e.paid_by?._id || e.paid_by || e.expensed_by;
            const expDate = e.expense_date
              ? new Date(e.expense_date).toISOString().split('T')[0]
              : e.date
              ? new Date(e.date).toISOString().split('T')[0]
              : e.created_at
              ? new Date(e.created_at).toISOString().split('T')[0]
              : '2026-08-01';

            const rawCat = (e.category || 'Other').toUpperCase();
            let normCat = 'Other';
            if (rawCat.includes('SUGAR')) normCat = 'Sugar';
            else if (rawCat.includes('MEDICINE')) normCat = 'Medicine';
            else if (rawCat.includes('LABOUR') || rawCat.includes('LABOR')) normCat = 'Labour';
            else if (rawCat.includes('TRANSPORT')) normCat = 'Transport';
            else if (rawCat.includes('TRAVEL')) normCat = 'TRAVEL';
            else if (rawCat.includes('MAINTENANCE')) normCat = 'Maintenance';
            else if (e.category) normCat = e.category;

            const supObj = memberList.find(s => String(s.id) === String(expMemberId));
            const hasAdv = advanceIssuedUserIds.has(String(expMemberId));
            const isAdvanceMode = hasAdv;
            const appStatus = (e.approval_status || e.approvalStatus || e.status || 'PENDING').toLowerCase();

            const repId = e.reporting_to?._id || e.reporting_to || e.supervisor_id || null;
            const repObj = repId ? memberList.find(s => String(s.id) === String(repId)) : null;
            const repName = e.reporting_to?.name || e.reporting_to_name || repObj?.name || null;

            const expApiaryId = e.apiary_id || e.apiaryId;
            const isApiaryExp = Boolean(expApiaryId);
            const targetApiary = isApiaryExp
              ? mappedApis.find(a => 
                  String(a.id).toLowerCase() === String(expApiaryId).toLowerCase() ||
                  String(a.apiary_id).toLowerCase() === String(expApiaryId).toLowerCase() ||
                  (a.mongoId && String(a.mongoId).toLowerCase() === String(expApiaryId).toLowerCase())
                )
              : null;
            const geoExpProjId = findProjectIdByGeo(e.state, e.district, `${e.remarks || ''} ${e.notes || ''} ${e.details || ''} ${supObj?.name || ''}`);
            const userProjId = e.project_id || e.projectId || targetApiary?.projectId || supObj?.projectId || geoExpProjId || null;
            const finalTagType = isApiaryExp ? 'apiary' : 'project_overhead';
            const finalLocId = isApiaryExp ? (targetApiary?.id || expApiaryId) : (userProjId ? String(userProjId) : 'overhead');

            const empIdVal = e.user_employee_id || e.employee_id || e.paid_by_employee_id || e.created_by_employee_id || supObj?.employee_id || null;

            return {
              id: e._id || e.expense_id || `EXP_${i}`,
              expense_id_display: e.expense_id,
              type: 'expense',
              date: expDate,
              memberId: expMemberId,
              memberName: e.paid_by?.name || e.paid_by_name || e.created_by_name || supObj?.name || 'Staff',
              user_employee_id: empIdVal,
              employee_id: empIdVal,
              paid_by_employee_id: empIdVal,
              reportingTo: repId,
              reportingToName: repName,
              amount: Number(e.amount) || 0,
              category: normCat,
              rawCategory: e.category || 'Other',
              tagType: finalTagType,
              locId: String(finalLocId),
              apiaryId: expApiaryId ? String(expApiaryId) : null,
              projectId: userProjId ? String(userProjId) : null,
              approvalStatus: appStatus,
              approved_by: e.approved_by || null,
              approved_by_name: e.approved_by_name || (typeof e.approved_by === 'object' ? (e.approved_by?.name || e.approved_by?.username) : null) || e.approved_by || null,
              approval_comment: e.approval_comment || '',
              approval_date: e.approval_date || null,
              receipt: e.receipt || (e.proof_urls && e.proof_urls.length > 0) || e.attachments ? true : false,
              proof_urls: e.proof_urls || (e.bill_url ? [e.bill_url] : []) || [],
              comment: e.remarks || e.notes || e.note || e.comments || '',
              fundingSource: isAdvanceMode ? 'advance' : 'reimbursement',
              // Real persisted field is payout_status (snake_case, 'PAID'/'UNPAID').
              // Original e.payoutStatus check kept as a fallback alongside it, though it
              // never actually existed on the raw object — payout_status is checked first.
              payoutStatus: isAdvanceMode ? 'na' : ((e.payout_status || e.payoutStatus || 'unpaid').toLowerCase()),
              travel_details: e.travel_details || null,
              rawExpense: e
            };
          });
          liveTxns = [...liveTxns, ...mappedExps];
        }

        setTxns(liveTxns);
      } catch (err) {
        console.error('Error loading backend reference data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadBackendData();
  }, []);

  const member = useCallback(id => members.find(m => m.id === id), [members]);
  const apiary = useCallback(id => apiaries.find(a => a.id === id), [apiaries]);
  const project = useCallback(id => projects.find(p => p.id === id), [projects]);

  const sum = (arr, f) => arr.reduce((s, x) => s + f(x), 0);

  const locLabel = useCallback(t => {
    if (t.tagType === 'apiary') {
      const ap = apiary(t.locId) || (t.apiaryId ? apiary(t.apiaryId) : null);
      return ap ? ap.name : (t.locId || 'Apiary');
    }
    const proj = project(t.projectId) || (t.locId && t.locId !== 'overhead' ? project(t.locId) : null);
    if (proj && proj.name) {
      return `${proj.name} (Overhead)`;
    }
    return 'Non-Apiary';
  }, [apiary, project]);

  const memberStats = useCallback(id => {
    const m = member(id);
    if (!m) return { mode: 'advance', balance: 0, overdrawn: false, converted: 0, unpaid: 0, paid: 0, credit: 0, pendingExpenses: 0, netWalletBalance: 0 };

    const advIssued = sum(txns.filter(t => t.type === 'advance_issued' && String(t.memberId) === String(id)), t => t.amount);
    const advSettled = sum(txns.filter(t => t.type === 'advance_settled' && String(t.memberId) === String(id)), t => t.amount);
    const advExpenseApproved = sum(txns.filter(t => t.type === 'expense' && String(t.memberId) === String(id) && t.approvalStatus === 'approved'), t => t.amount);
    const advExpensePending = sum(txns.filter(t => t.type === 'expense' && String(t.memberId) === String(id) && (t.approvalStatus === 'pending' || !t.approvalStatus)), t => t.amount);

    const raw = advIssued - advSettled - advExpenseApproved;
    const netWallet = raw - advExpensePending;
    const overdrawn = raw < 0;

    if (m.mode === 'advance') {
      return {
        mode: 'advance',
        balance: raw,
        pendingExpenses: advExpensePending,
        netWalletBalance: netWallet,
        overdrawn,
        converted: overdrawn ? Math.abs(raw) : 0,
        advIssued,
        advExpenseApproved
      };
    } else {
      const approved = txns.filter(t => t.type === 'expense' && String(t.memberId) === String(id) && t.approvalStatus === 'approved' && t.fundingSource === 'reimbursement');
      const unpaidItemized = sum(approved.filter(t => t.payoutStatus === 'unpaid'), t => t.amount);
      const paid = sum(approved.filter(t => t.payoutStatus === 'paid'), t => t.amount);
      const netAdjust = sum(txns.filter(t => t.type === 'adjustment' && String(t.memberId) === String(id)), t => t.amount);
      const rawPayable = unpaidItemized + netAdjust;
      const unpaid = Math.max(rawPayable, 0);
      const credit = rawPayable < 0 ? -rawPayable : 0;
      return {
        mode: 'reimbursement',
        balance: raw,
        pendingExpenses: advExpensePending,
        netWalletBalance: netWallet,
        unpaid,
        paid,
        unpaidItemized,
        netAdjust,
        credit
      };
    }
  }, [member, txns]);

  /* Decision Handler (Approve / Reject / Pending) */
  const handleDecide = async (id, decision, commentParam) => {
    const t = txns.find(x => x.id === id);
    if (!t) return;
    const isNonApiary = t.tagType === 'non_apiary';
    const normDecision = decision?.toLowerCase();

    if (normDecision === 'approved') {
      Swal.fire({
        title: 'Approve Expense?',
        text: `Approve ₹${Number(t.amount || 0).toLocaleString('en-IN')} for ${t.memberName || 'staff'}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, Approve',
        confirmButtonColor: '#16a34a',
        cancelButtonText: 'Cancel'
      }).then(async result => {
        if (result.isConfirmed || result.value) {
          try {
            await updateExpenseApprovalStatus(id, 'APPROVED', commentParam || '', 'Admin', isNonApiary);
            setTxns(prev =>
              prev.map(item =>
                item.id === id
                  ? {
                      ...item,
                      approvalStatus: 'approved',
                      approved_by_name: 'Admin',
                      payoutStatus: item.fundingSource === 'reimbursement' ? 'unpaid' : 'na'
                    }
                  : item
              )
            );
            toast.success('Expense approved successfully!');
          } catch (err) {
            console.error('Approval failed:', err);
            toast.error('Failed to approve expense');
          }
        }
      });
    } else if (normDecision === 'rejected') {
      if (commentParam !== undefined) {
        try {
          await updateExpenseApprovalStatus(id, 'REJECTED', commentParam, 'Admin', isNonApiary);
          setTxns(prev =>
            prev.map(item =>
              item.id === id
                ? { ...item, approvalStatus: 'rejected', approval_comment: commentParam, approved_by_name: 'Admin' }
                : item
            )
          );
          toast.error('Expense marked as REJECTED');
        } catch (err) {
          console.error('Rejection failed:', err);
          toast.error('Failed to reject expense');
        }
      } else {
        Swal.fire({
          title: 'Reason for rejection:',
          input: 'text',
          inputValue: t.comment || t.approval_comment || '',
          inputPlaceholder: 'Enter reason...',
          showCancelButton: true,
          confirmButtonText: 'Confirm Rejection',
          confirmButtonColor: '#D5372A',
          cancelButtonText: 'Cancel'
        }).then(async result => {
          if (result.value !== undefined && result.value !== null) {
            try {
              await updateExpenseApprovalStatus(id, 'REJECTED', result.value, 'Admin', isNonApiary);
              setTxns(prev =>
                prev.map(item =>
                  item.id === id
                    ? { ...item, approvalStatus: 'rejected', approval_comment: result.value, approved_by_name: 'Admin' }
                    : item
                )
              );
              toast.info('Expense rejected.');
            } catch (err) {
              console.error('Rejection failed:', err);
              toast.error('Failed to reject expense');
            }
          }
        });
      }
    } else if (normDecision === 'pending') {
      try {
        await updateExpenseApprovalStatus(id, 'PENDING', '', null, isNonApiary);
        setTxns(prev =>
          prev.map(item =>
            item.id === id
              ? { ...item, approvalStatus: 'pending', approved_by_name: null, approval_comment: '' }
              : item
          )
        );
        toast.info('Expense reset to PENDING');
      } catch (err) {
        console.error('Reset status failed:', err);
        toast.error('Failed to reset status');
      }
    }
  };

  /* Bulk Approve */
  const handleBulkApprove = async () => {
    const pendingIds = txns
      .filter(item => item.type === 'expense' && item.approvalStatus === 'pending')
      .map(item => item.id);

    if (pendingIds.length > 0) {
      try {
        await bulkApproveExpensesService(pendingIds);
      } catch (e) {
        console.warn('Bulk approve warning:', e);
      }
    }

    setTxns(prev =>
      prev.map(item => {
        if (item.type === 'expense' && item.approvalStatus === 'pending') {
          return {
            ...item,
            approvalStatus: 'approved',
            payoutStatus: item.fundingSource === 'reimbursement' ? 'unpaid' : 'na'
          };
        }
        return item;
      })
    );
    toast.success('All pending expenses approved!');
  };

  /* Mark Single Reimbursement Paid */
  const handleMarkSinglePaid = async txnId => {
    const t = txns.find(x => x.id === txnId);
    if (!t) return;
    try {
      await createPayrollBatchService({
        expense_ids: [txnId],
        member_ids: [t.memberId],
        total_amount: t.amount,
        period: new Date().toLocaleString('default', { month: 'short', year: 'numeric' })
      });
      setTxns(prev =>
        prev.map(item => (item.id === txnId ? { ...item, payoutStatus: 'paid' } : item))
      );
      toast.success('Reimbursement marked as paid.');
    } catch (err) {
      console.error('Mark paid failed:', err);
      toast.error('Failed to mark as paid — nothing was recorded.');
    }
  };

  /* Passbook Drawer Trigger */
  const handleOpenPassbook = mId => {
    setActivePassbookMemberId(mId);
    setDrawerOpen(true);
  };

  /* Add Entry Modal Trigger */
  const handleOpenAddEntry = (type = 'expense', mId = '') => {
    setAddEntryInitialType(type);
    setAddEntryInitialMemberId(mId);
    setAddEntryShow(true);
  };

  /* Map a member's raw role string (bee_mitra, field_view_app_admin, community, ...)
     to the ExpenseModel incurred_by_role enum (BOLT/SUPERVISOR/COMMUNITY), mirroring
     the same mapping FieldView mobile's AddExpense screen already uses. */
  const mapRoleForExpense = role => {
    const r = (role || '').toLowerCase();
    if (r.includes('community')) return 'COMMUNITY';
    if (r.includes('admin') || r.includes('supervisor')) return 'SUPERVISOR';
    return 'BOLT';
  };

  /* Save Entry Handler */
  const handleSaveEntry = async payload => {
    const m = member(payload.memberId);

    if (payload.type === 'advance_issued') {
      try {
        await addAdvance({
          issued_to: payload.memberId,
          amount: payload.amount,
          date: payload.date,
          apiary_id: payload.locId || null,
          comments: payload.comment || ''
        });
      } catch (e) {
        console.warn('Backend addAdvance warning:', e);
      }

      setTxns(prev => [{
        id: 'A' + Date.now().toString().slice(-4),
        type: payload.type,
        date: payload.date,
        memberId: payload.memberId,
        amount: payload.amount,
        locId: payload.locId,
        comment: payload.comment,
        fundingSource: m?.mode === 'advance' ? 'advance' : 'reimbursement',
        payoutStatus: 'na'
      }, ...prev]);
      toast.success('New entry added successfully!');
      return;
    }

    // payload.type === 'expense' — unlike advance_issued above, this must actually
    // persist before we show success or add anything to local state: this whole
    // handler used to just push local state regardless of any backend call, which is
    // the exact bug being fixed here.
    try {
      let created;
      if (payload.tagType === 'apiary') {
        created = await addExpense({
          category: payload.category,
          amount: payload.amount,
          expense_date: payload.date,
          incurred_by_role: mapRoleForExpense(m?.role),
          apiary_id: payload.locId,
          paid_by: payload.memberId,
          notes: payload.comment || undefined
        });
      } else {
        const res = await addNonApiaryExpense({
          user_id: payload.memberId,
          expense_date: payload.date,
          category: payload.category,
          amount: payload.amount,
          expensed_by: m?.name || '',
          details: payload.comment || ''
        });
        created = res?.expense || res;
      }

      const realId = created?._id || created?.expense_id || created?.id;

      setTxns(prev => [{
        id: realId || ('E' + Date.now().toString().slice(-4)),
        type: payload.type,
        date: payload.date,
        memberId: payload.memberId,
        memberName: m?.name,
        amount: payload.amount,
        category: payload.category,
        tagType: payload.tagType,
        locId: payload.locId,
        approvalStatus: 'pending',
        comment: payload.comment,
        fundingSource: m?.mode === 'advance' ? 'advance' : 'reimbursement',
        payoutStatus: 'na'
      }, ...prev]);
      toast.success('New entry added successfully!');
    } catch (err) {
      console.error('Failed to save expense entry:', err);
      toast.error(err?.response?.data?.error || 'Failed to save expense — nothing was recorded.');
    }
  };

  /* Batch Payroll Paid Handler */
  const handleMarkPayrollPaid = async memberIds => {
    const payableRows = txns.filter(
      item =>
        item.type === 'expense' &&
        memberIds.includes(item.memberId) &&
        item.fundingSource === 'reimbursement' &&
        item.approvalStatus === 'approved' &&
        item.payoutStatus === 'unpaid'
    );

    if (payableRows.length === 0) {
      toast.error('Nothing unpaid to mark for the selected member(s).');
      return;
    }

    const batchTotal = sum(payableRows, t => t.amount);
    const period = new Date().toLocaleString('default', { month: 'short', year: 'numeric' });

    try {
      const res = await createPayrollBatchService({
        expense_ids: payableRows.map(t => t.id),
        member_ids: memberIds,
        total_amount: batchTotal,
        period
      });
      const batchId = res?.data?.batch_id || res?.batch_id || 'B-' + Date.now().toString().slice(-5);

      setTxns(prev => {
        const payableIds = new Set(payableRows.map(t => t.id));
        let updated = prev.map(item => (payableIds.has(item.id) ? { ...item, payoutStatus: 'paid' } : item));

        memberIds.forEach(mId => {
          const stillOwed = sum(
            updated.filter(t => t.type === 'adjustment' && t.memberId === mId),
            t => t.amount
          );
          if (stillOwed > 0) {
            updated.push({
              id: 'ADJ' + Date.now() + mId,
              type: 'adjustment',
              date: new Date().toISOString().split('T')[0],
              memberId: mId,
              amount: -stillOwed,
              comment: 'Cleared via full settlement · ' + batchId
            });
          }
        });
        return updated;
      });

      setPayrollBatches(prev => [
        {
          id: batchId,
          period,
          date: new Date().toISOString().split('T')[0],
          memberIds,
          total: batchTotal,
          status: 'closed'
        },
        ...prev
      ]);

      toast.success(`Batch ${batchId} marked as paid!`);
    } catch (err) {
      console.error('Mark payroll paid failed:', err);
      toast.error('Failed to record payroll batch — nothing was marked paid.');
    }
  };

  /* Pay All For Single Member */
  const handlePayAllForMember = mId => {
    handleMarkPayrollPaid([mId]);
  };

  /* Open Settle Modal */
  const handleOpenSettle = mId => {
    setSettleMemberId(mId);
    setSettleModalShow(true);
  };

  /* Confirm Settle Handler */
  const handleConfirmSettle = async settleObj => {
    const { memberId: mId, amount, fullReset, reason } = settleObj;
    const m = member(mId);

    if (m?.mode === 'reimbursement') {
      const rows = txns
        .filter(
          t =>
            t.type === 'expense' &&
            t.memberId === mId &&
            t.fundingSource === 'reimbursement' &&
            t.approvalStatus === 'approved' &&
            t.payoutStatus === 'unpaid'
        )
        .sort((a, b) => a.date.localeCompare(b.date)); // oldest first
      const itemizedTotal = sum(rows, t => t.amount);

      if (rows.length === 0) {
        toast.error('Nothing unpaid to settle for this member.');
        return;
      }

      // Only whole expenses that fit within the entered amount actually get marked
      // paid — payout_status is all-or-nothing per expense, so a lump sum that doesn't
      // cover a full expense can't partially pay it. Oldest-first so a partial
      // settlement always clears the longest-outstanding items first. (Previously this
      // marked every unpaid expense paid regardless of the amount entered, and recorded
      // the shortfall as a "credit" — backwards, since underpaying should leave more
      // owed, not less.)
      const covered = [];
      let remaining = Number(amount) || 0;
      for (const row of rows) {
        if (row.amount <= remaining) {
          covered.push(row);
          remaining -= row.amount;
        } else {
          break;
        }
      }

      if (covered.length === 0) {
        toast.error(`₹${amount} doesn't cover even the oldest unpaid expense (₹${rows[0].amount}) — nothing was marked paid.`);
        return;
      }

      const coveredTotal = sum(covered, t => t.amount);

      try {
        await createPayrollBatchService({
          expense_ids: covered.map(t => t.id),
          member_ids: [mId],
          total_amount: coveredTotal,
          period: new Date().toLocaleString('default', { month: 'short', year: 'numeric' }),
          notes: reason || undefined
        });

        setTxns(prev => {
          const coveredIds = new Set(covered.map(t => t.id));
          return prev.map(t => (coveredIds.has(t.id) ? { ...t, payoutStatus: 'paid' } : t));
        });

        if (covered.length < rows.length) {
          toast.success(`Marked ${covered.length} of ${rows.length} expense(s) as paid (₹${coveredTotal} applied). ₹${itemizedTotal - coveredTotal} still unpaid.`);
        } else {
          toast.success('Settlement processed successfully!');
        }
      } catch (err) {
        console.error('Settle (reimbursement) failed:', err);
        toast.error('Failed to record settlement — nothing was recorded.');
      }
    } else {
      // Advance model settlement / reset
      const s = memberStats(mId);
      const resetAmt = fullReset ? s.balance : amount;

      try {
        await settleAdvanceService({
          member_id: mId,
          amount: resetAmt,
          notes: reason || 'Settlement / Reset',
          mode: fullReset ? 'full_reset' : 'partial'
        });

        setTxns(prev => [
          {
            id: 'ASET' + Date.now(),
            type: 'advance_settled',
            date: new Date().toISOString().split('T')[0],
            memberId: mId,
            amount: resetAmt,
            comment: reason || 'Settlement / Reset'
          },
          ...prev
        ]);
        toast.success('Settlement processed successfully!');
      } catch (err) {
        console.error('Settle (advance) failed:', err);
        toast.error('Failed to record settlement — nothing was recorded.');
      }
    }
  };

  if (loading) {
    return (
      <S.MainContainer style={{ minHeight: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#6B7280', padding: '60px' }}>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#181A20', marginBottom: '8px' }}>
            Loading live advances, expenses & balances...
          </div>
          <div style={{ fontSize: '13px' }}>Fetching records from backend database</div>
        </div>
      </S.MainContainer>
    );
  }

  return (
    <S.MainContainer>
      <S.TopBar>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 style={{ margin: 0 }}>{TITLES[activeTab][0]}</h1>
            {!isAdmin && (
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#64748b',
                  background: '#f1f5f9',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <i className="fa fa-lock" style={{ fontSize: '11px', color: '#94a3b8' }}></i> View-Only
              </span>
            )}
          </div>
          <p>{TITLES[activeTab][1]}</p>
        </div>
        {!['payroll', 'advances', 'expenses'].includes(activeTab) && isAdmin && (
          <S.Btn onClick={() => handleOpenAddEntry('expense')}>
            ＋ Add Entry
          </S.Btn>
        )}
      </S.TopBar>

      <S.NavSection>
        {TABS.map(tab => (
          <S.SubNavButton
            key={tab.id}
            active={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </S.SubNavButton>
        ))}
      </S.NavSection>

      {/* Tab Panels */}
      {activeTab === 'overview' && (
        <OverviewTab
          txns={txns}
          members={members}
          memberStats={memberStats}
          onOpenPassbook={handleOpenPassbook}
        />
      )}

      {activeTab === 'ledger' && (
        <LedgerTab
          txns={txns}
          members={members}
          apiaries={apiaries}
          projects={projects}
          member={member}
          apiary={apiary}
          project={project}
          locLabel={locLabel}
          isAdmin={isAdmin}
          onDecide={handleDecide}
          onViewDetails={t => setDetailModalItem(t)}
          onMarkSinglePaid={handleMarkSinglePaid}
          onOpenPassbook={handleOpenPassbook}
        />
      )}

      {activeTab === 'approvals' && (
        <ApprovalsTab
          txns={txns}
          member={member}
          locLabel={locLabel}
          isAdmin={isAdmin}
          onDecide={handleDecide}
          onViewDetails={t => setDetailModalItem(t)}
          onBulkApprove={handleBulkApprove}
          onOpenPassbook={handleOpenPassbook}
        />
      )}

      {activeTab === 'advances' && (
        <>
          <div style={{ display: 'flex', gap: '8px', margin: '4px 0 8px' }}>
            <button
              type="button"
              className={`btn btn-sm ${advanceView === 'issued' ? 'btn-dark' : 'btn-outline-secondary'}`}
              onClick={() => setAdvanceView('issued')}
            >
              Issued Advances
            </button>
            <button
              type="button"
              className={`btn btn-sm ${advanceView === 'requests' ? 'btn-dark' : 'btn-outline-secondary'}`}
              onClick={() => setAdvanceView('requests')}
            >
              Advance Requests
              {pendingAdvanceRequests > 0 && (
                <span className="badge badge-danger ml-2">{pendingAdvanceRequests}</span>
              )}
            </button>
          </div>
          {advanceView === 'issued' ? (
            <AdvancesTable isGlobal={false} isAdmin={isAdmin} />
          ) : (
            <AdvanceRequestsTab isAdmin={isAdmin} onPendingCount={setPendingAdvanceRequests} />
          )}
        </>
      )}

      {activeTab === 'expenses' && (
        <NectarExpenses isGlobal={false} isAdmin={isAdmin} />
      )}

      {activeTab === 'apiary' && (
        <ApiaryPnLTab
          txns={txns}
          apiaries={apiaries}
          projects={projects}
          member={member}
          project={project}
          onOpenPassbook={handleOpenPassbook}
        />
      )}

      {activeTab === 'project' && (
        <ProjectPnLTab
          txns={txns}
          projects={projects}
          apiaries={apiaries}
          member={member}
          onOpenPassbook={handleOpenPassbook}
        />
      )}

      {activeTab === 'payroll' && (
        <PayrollTab
          txns={txns}
          members={members}
          payrollBatches={payrollBatches}
          isAdmin={isAdmin}
          onMarkPayrollPaid={handleMarkPayrollPaid}
          onOpenPassbook={handleOpenPassbook}
        />
      )}

      {activeTab === 'reporting' && (
        <ReportingMappingTab
          members={members}
          projects={projects}
          isAdmin={isAdmin}
        />
      )}

      {/* Drawers & Modals */}
      <PassbookDrawer
        show={drawerOpen}
        memberId={activePassbookMemberId}
        members={members}
        txns={txns}
        memberStats={memberStats}
        apiary={apiary}
        locLabel={locLabel}
        isAdmin={isAdmin}
        onClose={() => setDrawerOpen(false)}
        onOpenAddEntry={handleOpenAddEntry}
        onOpenSettle={handleOpenSettle}
        onPayAllForMember={handlePayAllForMember}
      />

      <AddEntryModal
        show={addEntryShow}
        members={members}
        apiaries={apiaries}
        projects={projects}
        initialType={addEntryInitialType}
        initialMemberId={addEntryInitialMemberId}
        onClose={() => setAddEntryShow(false)}
        onSave={handleSaveEntry}
      />

      <SettleModal
        show={settleModalShow}
        memberId={settleMemberId}
        members={members}
        memberStats={memberStats}
        onClose={() => setSettleModalShow(false)}
        onConfirmSettle={handleConfirmSettle}
      />

      {detailModalItem && (
        <ExpenseDetailModal
          item={detailModalItem}
          isAdmin={isAdmin}
          onClose={() => setDetailModalItem(null)}
          onUpdateStatus={handleDecide}
        />
      )}
    </S.MainContainer>
  );
};

export default AdvancesExpensesHub;
